import React, { FunctionComponent, ReactNode, useState } from 'react'
import { useIntl } from 'react-intl'
import { Clipboard, TextStyle, View } from 'react-native'

import { FlattenSimpleInterpolation } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { BodyTextSize } from '../../styles'
import { tID } from '../../utils'
import { BodyText } from '../bodyText/BodyText'
import { CopyIcon } from '../icons'
import { Tooltip } from '../tooltip/Tooltip'

export interface CopyToClipboardProps {
  copyText: string
  fillColor?: string
  testID?: string
  size?: number
  displayText?: string | ReactNode
  displayTextSize?: BodyTextSize
  containerCustomStyle?: FlattenSimpleInterpolation
  accessibilityLabel?: string
  customTextStyle?: TextStyle
  onPress?: () => void
}

const DisplayTextContainer = styled(BodyText)(({ theme }) => ({
  color: theme.colors.iconActive,
  marginRight: '8px',
  marginTop: '-4px',
}))

const TextAndIconContainer = styled(View)<{ customStyle?: FlattenSimpleInterpolation }>`
  display: flex;
  flex-direction: row;
  ${({ customStyle }) => customStyle}
`

export const CopyToClipboard: FunctionComponent<CopyToClipboardProps> = ({
  copyText,
  fillColor,
  testID = 'CopyToClipboard',
  size = 18,
  displayText,
  displayTextSize,
  containerCustomStyle,
  accessibilityLabel,
  customTextStyle,
  onPress,
}) => {
  const [copied, setCopied] = useState(false)
  const { colors } = useTheme()
  const { formatMessage } = useIntl()

  const copyToClipboard = () => {
    onPress?.()
    setCopied(true)
    Clipboard.setString(copyText)
    setTimeout(() => {
      setCopied(false)
    }, 2000)
  }

  return (
    <Tooltip
      content={
        copied
          ? formatMessage({ defaultMessage: 'Copied', description: 'Tooltip text shown after copying to clipboard' })
          : formatMessage({ defaultMessage: 'Copy to Clipboard', description: 'Tooltip text for copy to clipboard' })
      }
      placement='right'
      hoverEnabled
      onPress={copyToClipboard}
      testID={tID(testID)}
      accessibilityLabel={
        accessibilityLabel ??
        formatMessage({
          defaultMessage: 'Copy to Clipboard',
          description: 'Accessibility label for copy to clipboard button',
        })
      }
    >
      {displayText ? (
        <TextAndIconContainer customStyle={containerCustomStyle}>
          <DisplayTextContainer size={displayTextSize || BodyTextSize.DEFAULT} style={customTextStyle}>
            {displayText}
          </DisplayTextContainer>
          <CopyIcon fillColor={fillColor || colors.iconActive} size={size} />
        </TextAndIconContainer>
      ) : (
        <CopyIcon fillColor={fillColor || colors.iconActive} size={size} />
      )}
    </Tooltip>
  )
}
